import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import { useCalendar } from '../../contexts/CalendarContext';
import CreateEventModal from './CreateEventModal';
import DayEventsModal from './DayEventsModal';

const CalendarView = ({ calendar }) => {
  const { t, i18n } = useTranslation('calendar');
  const dateLocale = getDateFnsLocale(i18n.language);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const { createEvent, getEventsForDate } = useCalendar();

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const weekDays = t('calendarView.weekDays', { returnObjects: true });

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());
  const isToday = (day) => isSameDay(day, new Date());

  const handleDayClick = (day) => {
    setSelectedDate(day);
    setShowDayEventsModal(true);
  };

  const handleNewEventClick = () => {
    setSelectedDate(new Date());
    setShowEventModal(true);
  };

  const handleCreateEvent = (eventData) => {
    createEvent(eventData);
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-[var(--color-bg-base)]">
      {/* Header: breadcrumb + title + Yeni Etkinlik */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200 dark:border-white/10 bg-white dark:bg-[var(--color-bg-base)]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col min-w-0">
            <h1 className="text-xl font-bold text-slate-900 dark:text-white truncate">
              {format(currentDate, 'MMMM yyyy', { locale: dateLocale })}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={goToPreviousMonth}
              className="p-2 rounded-md border border-slate-200 dark:border-white/10 text-slate-700 dark:text-[var(--color-text-secondary)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-elevated)] transition-colors"
              aria-label={t('calendarView.previousMonth')}
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 dark:border-white/10 text-slate-700 dark:text-[var(--color-text-secondary)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-elevated)] transition-colors"
            >
              {t('calendarView.today')}
            </button>
            <button
              onClick={goToNextMonth}
              className="p-2 rounded-md border border-slate-200 dark:border-white/10 text-slate-700 dark:text-[var(--color-text-secondary)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-elevated)] transition-colors"
              aria-label={t('calendarView.nextMonth')}
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button
              onClick={handleNewEventClick}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md px-4 py-2 text-sm transition-colors flex-shrink-0"
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              {t('calendarView.newEvent')}
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        <div className="grid grid-cols-7 border border-slate-200 dark:border-white/10 rounded-md overflow-hidden bg-slate-200 dark:bg-[var(--color-bg-elevated)]" style={{ gridTemplateRows: 'auto 1fr' }}>
          {/* Week day headers */}
          {weekDays.map((day) => (
            <div
              key={day}
              className="bg-white dark:bg-[var(--color-bg-base)] text-center text-xs font-semibold text-slate-600 dark:text-[var(--color-text-secondary)] uppercase tracking-wider py-3 border-b border-slate-200 dark:border-white/10"
            >
              {day}
            </div>
          ))}

          {/* Day cells */}
          {days.map((day) => {
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isCurrentDay = isToday(day);
            const dayEvents = calendar ? getEventsForDate(day, calendar.id) : [];

            return (
              <div
                key={day.toISOString()}
                onClick={() => handleDayClick(day)}
                className={`
                  min-h-[80px] p-2 cursor-pointer transition-colors flex flex-col
                  bg-white dark:bg-[var(--color-bg-base)]
                  hover:bg-slate-50 dark:hover:bg-[var(--color-bg-elevated)]
                  border-b border-r border-slate-200 dark:border-white/10
                  ${!isCurrentMonth ? 'opacity-60' : ''}
                  ${isCurrentDay ? 'ring-inset ring-2 ring-blue-500 dark:ring-blue-500' : ''}
                `}
              >
                <span
                  className={`text-sm font-medium ${
                    isCurrentDay
                      ? 'text-blue-600 dark:text-blue-400 font-bold'
                      : isCurrentMonth
                        ? 'text-slate-900 dark:text-white'
                        : 'text-slate-400 dark:text-[var(--color-text-secondary)]'
                  }`}
                >
                  {format(day, 'd')}
                </span>
                <div className="flex-1 overflow-y-auto space-y-1 mt-1">
                  {dayEvents.slice(0, 2).map((event, idx) => (
                    <div
                      key={event.id || idx}
                      className="px-1.5 py-0.5 rounded-md text-[10px] font-medium truncate bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDate(day);
                        setShowDayEventsModal(true);
                      }}
                    >
                      {event.title || ''}
                    </div>
                  ))}
                  {dayEvents.length > 2 && (
                    <div className="text-[10px] px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-[var(--color-bg-elevated)] text-slate-600 dark:text-[var(--color-text-secondary)] font-medium">
                      {t('calendarView.moreEvents', { count: dayEvents.length - 2 })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <DayEventsModal
        isOpen={showDayEventsModal}
        onClose={() => {
          setShowDayEventsModal(false);
          setSelectedDate(null);
        }}
        selectedDate={selectedDate}
        calendar={calendar}
      />

      <CreateEventModal
        isOpen={showEventModal}
        onClose={() => {
          setShowEventModal(false);
          setSelectedDate(null);
        }}
        onCreate={handleCreateEvent}
        selectedDate={selectedDate}
        calendar={calendar}
      />
    </div>
  );
};

export default CalendarView;
